import JsBarcode from "jsbarcode";
import { useEffect, useRef, useMemo } from "react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, AlertCircle } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { boletosApi, BoletoData, Parcelas } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { useQuery } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";
import { LoadingState } from "@/pages/CustomerPortal/components/LoadingState";
import { format, parseISO } from "date-fns";

interface BoletoPros {
    installment: Parcelas;
    cnpj: string | number;
    date: DateRange;
    setInstallment: () => void
}

export const Boleto = ({ cnpj, date, installment, setInstallment }: BoletoPros) => {
    const hasBoletoHtml = !!installment.parmBase64PdfBoleto;
    const consolidatedLines = installment.lines || [];

    return (
        <Dialog open={!!installment} onOpenChange={setInstallment}>
            <DialogContent className="max-w-4xl p-0 print:shadow-none print:border-none print:!p-0 flex flex-col max-h-[95vh]">
                <DialogHeader className="p-6 pb-0 no-print">
                    <DialogTitle>Boleto Bancário</DialogTitle>
                    <DialogDescription>
                        Confira os dados antes de efetuar o pagamento.
                    </DialogDescription>
                </DialogHeader>

                {/* {consolidatedLines.length > 1 && (
                    <div className="mx-6 mb-2 no-print rounded-lg border border-amber-300 bg-amber-50 p-4">
                        <p className="text-sm font-bold text-amber-800">
                            Este boleto é único e cobre {consolidatedLines.length} pedidos. Pague apenas uma vez.
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-amber-700">
                            {consolidatedLines.map((line, index) => (
                                <li key={`${line.orderId || line.invoiceId}-${index}`} className="flex justify-between gap-4">
                                    <span>Pedido {line.orderNumber || "---"} · NF {line.invoiceId || "---"}</span>
                                    <span className="font-semibold">
                                        {formatCurrency(line.newAmount > 0 ? line.newAmount : line.oldAmount)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )} */}

                {hasBoletoHtml ? (
                    <BoletoHtmlContent
                        htmlContent={installment.parmBase64PdfBoleto!}
                        setInstallment={setInstallment}
                    />
                ) : (
                    <BoletoQueryContent
                        installment={installment}
                        cnpj={cnpj}
                        date={date}
                        setInstallment={setInstallment}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

/**
 * Renderiza o HTML do boleto que veio no campo parmBase64PdfBoleto
 * usando um iframe para isolamento de estilos.
 */
const BoletoHtmlContent = ({
    htmlContent,
    setInstallment,
}: {
    htmlContent: string;
    setInstallment: () => void;
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const handlePrintHtml = () => {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) {
            toast.error("Erro de impressão", { description: "Conteúdo do boleto não encontrado para impressão." });
            return;
        }
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    };

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const writeContent = () => {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) return;

            doc.open();
            doc.write(htmlContent);
            doc.close();

            // Ajusta altura do iframe ao conteúdo após carregamento
            const adjustHeight = () => {
                const body = doc.body;
                const html = doc.documentElement;
                if (body && html) {
                    const height = Math.max(
                        body.scrollHeight,
                        body.offsetHeight,
                        html.clientHeight,
                        html.scrollHeight,
                        html.offsetHeight
                    );
                    iframe.style.height = `${height + 20}px`;
                }
            };

            // Ajusta após imagens carregarem
            const images = doc.querySelectorAll('img');
            if (images.length > 0) {
                let loaded = 0;
                images.forEach((img) => {
                    if (img.complete) {
                        loaded++;
                        if (loaded === images.length) adjustHeight();
                    } else {
                        img.addEventListener('load', () => {
                            loaded++;
                            if (loaded === images.length) adjustHeight();
                        });
                    }
                });
            }

            // Ajusta imediatamente e após um pequeno delay
            adjustHeight();
            setTimeout(adjustHeight, 300);
        };

        writeContent();
    }, [htmlContent]);

    return (
        <>
            <div className="overflow-y-auto p-6 min-h-[300px] flex flex-col">
                <iframe
                    ref={iframeRef}
                    id="boleto-container"
                    className="w-full border-0 bg-white rounded"
                    style={{ minHeight: '500px' }}
                    title="Boleto Bancário"
                />
            </div>
            <DialogFooter className="p-6 pt-0 no-print">
                <Button variant="outline" onClick={setInstallment}>Fechar</Button>
                <Button onClick={handlePrintHtml}>
                    <Download className="h-4 w-4 mr-2" />
                    Imprimir / Salvar PDF
                </Button>
            </DialogFooter>
        </>
    );
};

/**
 * Lógica original: busca dados via useQuery e renderiza com BoletoLayout.
 * Usado como fallback quando parmBase64PdfBoleto não está disponível.
 */
const BoletoQueryContent = ({
    installment,
    cnpj,
    date,
    setInstallment,
}: {
    installment: Parcelas;
    cnpj: string | number;
    date: DateRange;
    setInstallment: () => void;
}) => {
    const extrairNumeros = (texto: string | number) => `${texto}`.replace(/\D/g, '');

    const {
        data: boletoData,
        isError,
        isLoading,
    } = useQuery<BoletoData | null>({
        queryKey: ['boleto', installment, cnpj],
        queryFn: async () => {
            // Se já temos os dados no installment (parmJournalTransRecId > 0), retornamos um objeto compatível
            if (installment.parmJournalTransRecId && installment.parmJournalTransRecId > 0) {
                return {
                    journalTransRecId: installment.parmJournalTransRecId,
                    numeroDocumento: installment.parmBltNumeroDocumento || '',
                    codigoBarras: installment.parmBltCodBarras || '',
                    bankName: installment.parmBltCedenteNome || 'BANCO ITAU',
                    linhaDigitavel: installment.linhaDigitavel || '', // Ajustar se houver campo específico
                    dataEmissao: installment.parmBltDataEmissao || installment.dueDate,
                    dataVencimento: installment.parmBltDataVencimento || installment.dueDate,
                    valorTitulo: installment.parmBltValorTitulo || installment.oldAmount,
                    valorOriginal: installment.oldAmount,
                    valorAtualizado: installment.newAmount,
                    sacadoNome: installment.parmBltSacadoNome || '',
                    sacadoCnpjCpf: installment.parmBltSacadoCnpjCpf || '',
                    sacadoEndereco: installment.parmBltEnderecoSacadoEndereco || '',
                    sacadoNumero: installment.parmBltEnderecoSacadoNumero || '',
                    sacadoBairro: installment.parmBltEnderecoSacadoBairro || '',
                    sacadoCidade: installment.parmBltEnderecoSacadoCidade || '',
                    sacadoUf: installment.parmBltEnderecoSacadoUf || '',
                    sacadoCep: installment.parmBltEnderecoSacadoCep || '',
                    cedenteNome: installment.parmBltCedenteNome || '',
                    cedenteCnpjCpf: installment.parmBltCedenteCnpjCpf || '',
                    bankCode: installment.parmBltBankCode || '341',
                } as BoletoData;
            }

            const { data } = await boletosApi.getData(installment.parmJournalTransRecId, extrairNumeros(cnpj), {
                from: format(date.from, 'yyyy-MM-dd'),
                to: format(date.to, 'yyyy-MM-dd')
            });

            return data
        },
        enabled: !!installment,
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (isError) {
            toast.error("Erro ao gerar boleto", { description: "Não foi possível obter os dados do boleto da API." });
            setInstallment();
        }
    }, [isError, setInstallment]);

    const selectedBoleto = useMemo(() => {
        if (!boletoData || !boletoData.journalTransRecId) return null;

        const { bankCode, bankName, journalTransRecId, cedenteCnpjCpf } = boletoData;
        const vencimentoDate = boletoData.dataVencimento ? (typeof boletoData.dataVencimento === 'string' ? parseISO(boletoData.dataVencimento) : new Date(boletoData.dataVencimento)) : new Date();

        return {
            bankCode,
            bankName,
            cedenteCnpjCpf,
            journalTransRecId,
            cedente: boletoData.cedenteNome,
            agenciaCodigoCedente: `${boletoData.linhaDigitavel}`,
            sacado: boletoData.sacadoNome,
            dataEmissao: boletoData.dataEmissao,
            cnpjSacado: boletoData.sacadoCnpjCpf,
            vencimento: formatDate(vencimentoDate),
            valor: boletoData.valorTitulo,
            endereco: `
                    ${boletoData.sacadoEndereco}, ${boletoData.sacadoNumero},
                    ${boletoData.sacadoBairro}, ${boletoData.sacadoCidade}/${boletoData.sacadoUf}
                    CEP: ${boletoData.sacadoCep}
                `,
            nossoNumero: boletoData.numeroDocumento,
            linhaDigitavel: boletoData.linhaDigitavel,
            codigoBarras: boletoData.codigoBarras,
            valorCorrigido: installment.newAmount,
            localPagamento: "ATÉ O VENCIMENTO EM QUALQUER BANCO. APÓS O VENCIMENTO SOMENTE NO ITAÚ.",
            instrucoes: [
                `Após ${formatDate(vencimentoDate)}, multa de 2% e juros de 1% ao mês.`,
                `Sr. Caixa não receber após 30 dias do Vencimento.`,
                `Boleto referente à ${installment.monthYear}. `,
                `Parcela ${installment.monthYear.split('-')[1] || ''}`
            ],
        };
    }, [boletoData, installment]);

    const handlePrint = () => {
        const node = document.getElementById('boleto-container');
        if (!node) {
            toast.error("Erro de impressão", { description: "Conteúdo do boleto não encontrado para impressão." });
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none'; // Keep it hidden
        document.body.appendChild(iframe);

        const printDocument = iframe.contentWindow.document;
        printDocument.open();
        printDocument.write('<html><head><title>Imprimir Boleto</title>');

        // Copy all <link> and <style> tags from the main document's <head>
        const headTags = document.querySelectorAll('head > link[rel="stylesheet"], head > style');
        headTags.forEach(tag => {
            printDocument.write(tag.outerHTML);
        });

        // Add print-specific styles for A4 and margins
        printDocument.write('<style>@media print { body { margin: 1.5cm; width: 21cm; box-sizing: border-box; } }</style>');

        printDocument.write('</head><body>');
        printDocument.write(node.outerHTML); // Use outerHTML to include the #boleto-container itself
        printDocument.write('</body></html>');
        printDocument.close();

        iframe.contentWindow.focus();
        // Use a timeout to ensure styles are loaded before printing
        setTimeout(() => {
            iframe.contentWindow.print();
            document.body.removeChild(iframe); // Clean up the iframe
        }, 500); // 500ms delay might be needed for styles to apply
    };

    return (
        <>
            <div className="overflow-y-auto p-6 min-h-[300px] flex flex-col">
                {isLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-12">
                        <LoadingState variant="table" count={4} />
                        <p className="mt-4 text-sm text-muted-foreground animate-pulse">Carregando dados do boleto...</p>
                    </div>
                ) : isError ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                        <h3 className="text-lg font-bold">Erro ao carregar boleto</h3>
                        <p className="text-sm text-muted-foreground mt-2">Ocorreu um problema ao buscar os dados necessários.</p>
                        <Button variant="outline" className="mt-6" onClick={setInstallment}>Fechar</Button>
                    </div>
                ) : (
                    <>
                        {selectedBoleto ? (
                            <BoletoLayout boleto={selectedBoleto} />
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
                                <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
                                <h3 className="text-lg font-bold">Dados não encontrados</h3>
                                <p className="text-sm text-muted-foreground mt-2">Não foi possível processar as informações do boleto.</p>
                                <Button variant="outline" className="mt-6" onClick={setInstallment}>Fechar</Button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <DialogFooter className="p-6 pt-0 no-print">
                <Button variant="outline" onClick={setInstallment}>Fechar</Button>
                <Button onClick={handlePrint} disabled={!selectedBoleto}>
                    <Download className="h-4 w-4 mr-2" />
                    Imprimir / Salvar PDF
                </Button>
            </DialogFooter>
        </>
    );
};

const BoletoLayout = ({ boleto }) => {
    if (!boleto) return null;
    const barcodeRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (barcodeRef.current && boleto?.codigoBarras) {
            JsBarcode(barcodeRef.current, boleto.codigoBarras, {
                displayValue: false,
                height: 50,
                width: 2,
                margin: 0,
            });
        }
    }, [boleto]);

    const BoletoRow = ({ children }) => <div className="grid grid-cols-12 border-t border-gray-400">{children}</div>;
    const BoletoField = ({ label, value, colSpan = 4, isBold = true, align = 'left' }) => (
        <div className={`p-1.5 border-l border-gray-400 col-span-${colSpan}`}>
            <div className="text-xs text-gray-600">{label}</div>
            <div className={`text-sm ${isBold ? 'font-bold' : ''} text-${align}`}>{value}</div>
        </div>
    );

    return (
        <div id="boleto-container" className="bg-white text-black text-sm rounded-lg border border-gray-400">
            {/* Header */}
            <div className="flex justify-between items-center p-2 border-b-2 border-black">
                <div className="flex items-center gap-4">
                    <img src="/assets/img/itaulogo.png" alt="Logo Conexia" className="h-14" />
                    <span className="text-xl font-bold border-l-2 border-r-2 border-black px-4">341-7</span>
                </div>
                <p className="text-lg font-mono font-bold tracking-wider">{boleto.linhaDigitavel}</p>
            </div>

            {/* Body */}
            <div className="grid grid-cols-12">
                <div className="p-1.5 col-span-8">
                    <div className="text-xs text-gray-600">Local de Pagamento</div>
                    <div className="text-sm font-bold">{boleto.localPagamento}</div>
                </div>
                <div className="p-1.5 border-l bg-muted border-gray-400 col-span-4">
                    <div className="text-xs text-gray-600">Vencimento</div>
                    <div className="text-sm font-bold text-right">{boleto.vencimento}</div>
                </div>
            </div>
            <BoletoRow>
                <BoletoField label="Cedente" value={<span>{boleto.cedente} <br /> <span className="text-[12px] text-gray-600">CNPJ: {boleto.cedenteCnpjCpf}</span></span>} colSpan={8} />
                <BoletoField label="Agência / Código do Beneficiário" value={`0125 / 88245-6`} isBold={false} colSpan={4} />
            </BoletoRow>
            <BoletoRow>
                <BoletoField label="Data do Documento" value={formatDate(new Date())} colSpan={2} isBold={false} />
                <BoletoField label="Nº do Documento" value={boleto.nossoNumero} colSpan={4} isBold={false} />
                <BoletoField label="Espécie doc." value="DS" colSpan={2} isBold={false} />
                <BoletoField label="Aceite" value="N" colSpan={1} isBold={false} />
                <BoletoField label="Data Processamento" value={formatDate(new Date(boleto.dataEmissao))} colSpan={2} isBold={false} align="right" />
            </BoletoRow>
            <BoletoRow>
                <BoletoField label="Número" value={boleto.journalTransRecId} colSpan={2} isBold={false} />
                <BoletoField label="Espécie" value="R$" colSpan={2} isBold={false} />
                <BoletoField label="Quantidade" value="" colSpan={2} isBold={false} />
                <BoletoField label="(=) Valor do Documento" value={formatCurrency(boleto.valor)} colSpan={4} />
            </BoletoRow>
            <div className="grid grid-cols-12">
                <div className="p-1.5 col-span-8 row-span-5 border-t border-gray-400">
                    <div className="text-xs text-gray-600">Instruções (Texto de responsabilidade do cedente)</div>
                    {boleto.instrucoes.map((inst, i) => <p key={i} className="text-xs font-semibold">{inst}</p>)}
                </div>
                <div className="p-1.5 border-l border-t border-gray-400 col-span-4 h-12">
                    <div className="text-xs text-gray-600">(-) Desconto / Abatimento</div>
                </div>
                <div className="p-1.5 border-l border-t border-gray-400 col-span-4 h-12">
                    <div className="text-xs text-gray-600">(-) Outras deduções</div>
                </div>
                <div className="p-1.5 border-l border-t border-gray-400 col-span-4 h-12">
                    <div className="text-xs text-gray-600">(+) Mora / Multa</div>
                    {boleto.valorCorrigido && (
                        <p className="text-xs mt-2 font-semibold">{formatCurrency((boleto.valorCorrigido - boleto.valor))}</p>
                    )}
                </div>
                <div className="p-1.5 border-l border-t border-gray-400 col-span-4 h-12">
                    <div className="text-xs text-gray-600">(+) Outros acréscimos</div>
                </div>
                <div className="p-1.5 border-l border-t border-gray-400 col-span-4 h-12">
                    <div className="text-xs text-gray-600">(=) Valor cobrado</div>
                    {boleto.valorCorrigido && (
                        <p className="text-xs mt-2 font-semibold">{formatCurrency(boleto.valorCorrigido)}</p>
                    )}
                </div>
            </div>
            <BoletoRow>
                <div className="p-1.5 col-span-12">
                    <div className="text-xs text-gray-600">Pagador:</div>
                    <div className="text-sm font-bold">
                        {boleto.sacado} - CNPJ: {boleto.cnpjSacado} <br />
                    </div>
                    <div className="text-sm">
                        {boleto.endereco}
                    </div>
                </div>
            </BoletoRow>

            {/* Barcode */}
            <div className="p-4 w-full flex flex-col gap-3">
                <div className="w-full">
                    <svg ref={barcodeRef} className="barcode"></svg>
                    <span className=" text-gray-700">
                        {boleto.linhaDigitavel}
                    </span>
                </div>
                <div>
                </div>
            </div>
        </div>
    );
};